import { memo, type ReactNode } from 'react';

import { EllipsisVerticalIcon, XIcon } from '@air/design-icons';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { SelectionActionBarItemCount } from '~/components/v2/components/shared/selectionBar/SelectionActionBarItemCount';
import { type SelectionActionBarItemCountState } from '~/components/v2/components/shared/selectionBar/selectionBarItemCountState';
import { SELECTION_ACTION_BAR_ID } from '~/constants/selectionActionBar';

export type SelectionActionBarPanelProps = {
  itemCount: SelectionActionBarItemCountState;
  menuOptions: DropdownMenuOption[];
  onClearAll: () => void;
  leftBarButtons?: ReactNode;
  stackLeftBarButtons?: boolean;
  prefixBarButtons?: ReactNode;
  suffixBarButtons?: ReactNode;
  summary: string;
};

/**
 * The default selection bar's frosted-glass panel — clear, count, ellipsis menu, and action slots.
 * Portal placement and visibility gating live in the smart wrappers above it.
 */
export const SelectionActionBarPanel = memo(
  ({
    itemCount,
    menuOptions,
    onClearAll,
    leftBarButtons,
    stackLeftBarButtons,
    prefixBarButtons,
    suffixBarButtons,
    summary,
  }: SelectionActionBarPanelProps) => {
    return (
      <div
        className="fade-in slide-in-from-bottom-4 z-0 flex w-full min-w-0 animate-in items-center justify-between gap-4 overflow-hidden rounded-8 ads:bg-ads-gray-12/80 bg-gray-12/80 p-4 ads:text-ads-gray-1 text-gray-1 backdrop-blur-[18px] duration-300"
        data-disableselect="true"
        data-testid="SELECTION_ACTION_BAR"
        id={SELECTION_ACTION_BAR_ID}
      >
        {stackLeftBarButtons && leftBarButtons ? (
          <div
            className="grid min-w-0 shrink-0 grid-cols-[auto,1fr] grid-rows-[auto,auto] items-start gap-x-4 gap-y-0"
            data-testid="SELECTION_ACTION_BAR_LEFT_GROUP"
          >
            <div className="row-span-2 self-center">
              <IconButton
                adsIcon
                appearance="ghost"
                className="shrink-0 rounded-4 ads:text-ads-gray-1 text-gray-1 ads:hover:text-icon-default hover:text-gray-10 active:text-gray-9 ads:active:text-icon-subtle"
                color="gray"
                data-testid="SELECTION_ACTION_BAR_CLEAR_BTN"
                icon={XIcon}
                label="Clear all"
                onClick={onClearAll}
                size="small"
              />
            </div>
            <div className="self-center">
              <SelectionActionBarItemCount {...itemCount} summary={summary} />
            </div>
            {leftBarButtons ? <div className="flex min-w-0 items-start gap-2">{leftBarButtons}</div> : null}
          </div>
        ) : (
          <div className="flex min-w-0 shrink-0 items-center gap-4" data-testid="SELECTION_ACTION_BAR_LEFT_GROUP">
            <IconButton
              adsIcon
              appearance="ghost"
              className="shrink-0 rounded-4 ads:text-ads-gray-1 text-gray-1 ads:hover:text-icon-default hover:text-gray-10 active:text-gray-9 ads:active:text-icon-subtle"
              color="gray"
              data-testid="SELECTION_ACTION_BAR_CLEAR_BTN"
              icon={XIcon}
              label="Clear all"
              onClick={onClearAll}
              size="small"
            />
            <SelectionActionBarItemCount {...itemCount} summary={summary} />
            {leftBarButtons ? <div className="flex min-w-0 items-center gap-4">{leftBarButtons}</div> : null}
          </div>
        )}
        <div className="flex min-w-0 shrink items-center justify-end gap-4 overflow-hidden">
          <div className="flex min-w-0 shrink items-center gap-4 overflow-hidden">{prefixBarButtons}</div>

          {menuOptions.length > 0 && (
            <DropdownMenu
              sideOffset={16}
              className="w-[240px]"
              trigger={
                <IconButton
                  adsIcon
                  appearance="ghost"
                  className="shrink-0 rounded-4 ads:text-ads-gray-1 text-gray-1 ads:hover:text-icon-default hover:text-gray-10 active:text-gray-9 ads:active:text-icon-subtle"
                  color="gray"
                  data-testid="SELECTION_ACTION_BAR_MORE_ACTIONS"
                  icon={EllipsisVerticalIcon}
                  label="More actions"
                  size="small"
                />
              }
            >
              {renderDropdownItems({ options: menuOptions })}
            </DropdownMenu>
          )}
          <div className="flex shrink-0 gap-2">{suffixBarButtons}</div>
        </div>
      </div>
    );
  },
);

SelectionActionBarPanel.displayName = 'SelectionActionBarPanel';
